// Study Schedule Types
export interface IContest {
  _id: string
  name: string
  icon: string
  publicTender: string
  career: string
  role: string
  situation: string
}

export interface IDiscipline {
  ref: string
  module: string
  order: number
  subject: string
}

export interface IDisciplineSelection extends IDiscipline {
  knowledgeLevel: TKnowledgeLevel
  relevanceLevel: TRelevanceLevel
  isSelected: boolean
  weight: number
}

export interface ISessionDuration {
  minutes: number
}

export interface ISessionDurationRange {
  minDuration: number // in minutes
  maxDuration: number // in minutes
}

export interface IStudyScheduleData {
  contest: IContest | null
  disciplines: IDisciplineSelection[]
  sessionDuration: ISessionDuration
  weeklySchedule: IWeeklySchedule
}

export interface IStudyScheduleSummary {
  totalWeeklySessions: number
  totalWeeklyHours: number
  selectedDays: string[]
  selectedDisciplines: number
  motivationalMessage: string
}

export interface IContestApiResponse {
  student: {
    _id: string
    subscriptions: Array<{
      _id: string
      type: string
      name: string
    }>
    courses: Array<{
      _id: string
      type: string
      name: string
      publicTender: {
        _id: string
        name: string
        icon: string
      }
      contentType: string
    }>
  }
  data: IContest[]
  metadata: {
    limit: number
    page: number
    total: number
    totalPages: number
    hasPrevPage: boolean
    hasNextPage: boolean
    prevPage: number | null
    nextPage: number | null
  }
}

export type IDisciplineApiResponse = IDiscipline[]

// Component Props
export interface IStudyScheduleWizardProps {
  onComplete: (data: IStudyScheduleData) => void
  onCancel: () => void
}

export interface IContestSelectionProps {
  selectedContest: IContest | null
  onContestSelect: (contest: IContest) => void
  onNext: () => void
}

export interface IDisciplineSelectionProps {
  disciplines: IDisciplineSelection[]
  onDisciplineChange: (disciplines: IDisciplineSelection[]) => void
  onNext: () => void
  onBack: () => void
}

export interface ISessionDurationProps {
  duration: ISessionDuration
  onDurationChange: (duration: ISessionDuration) => void
  onNext: () => void
  onBack: () => void
}

export interface IWeeklyScheduleProps {
  weeklySchedule: IWeeklySchedule
  onScheduleChange: (schedule: IWeeklySchedule) => void
  onNext: () => void
  onBack: () => void
}

export interface IScheduleSummaryProps {
  summary: IStudyScheduleSummary
  onComplete: () => void
  onBack: () => void
}

// Accordion Step Types
export type TAccordionStep =
  | 'contest-selection'
  | 'discipline-selection'
  | 'session-duration'
  | 'weekly-schedule'
  | 'summary'

export interface IAccordionStep {
  id: TAccordionStep
  title: string
  description: string
  isCompleted: boolean
  isActive: boolean
  isDisabled: boolean
}

// Knowledge Level Options
export const KNOWLEDGE_LEVELS = [
  { value: 0, label: 'Nenhum' },
  { value: 1, label: 'Básico' },
  { value: 2, label: 'Intermediário' },
  { value: 3, label: 'Avançado' },
  { value: 4, label: 'Especialista' },
  { value: 5, label: 'Mestre' },
] as const

export type TKnowledgeLevel = (typeof KNOWLEDGE_LEVELS)[number]['value']

export const RELEVANCE_LEVELS = [
  { value: 1, label: 'Mínima' },
  { value: 2, label: 'Baixo' },
  { value: 3, label: 'Médio' },
  { value: 4, label: 'Alto' },
  { value: 5, label: 'Máxima' },
] as const

export type TRelevanceLevel = (typeof RELEVANCE_LEVELS)[number]['value']

// Session Duration Options
export const SESSION_DURATIONS = [
  { value: 30, label: '30 min' },
  { value: 60, label: '60 min' },
  { value: 90, label: '90 min' },
  { value: 120, label: '120 min' },
] as const

export type TSessionDuration = (typeof SESSION_DURATIONS)[number]['value']

// Week Days
export const WEEK_DAYS = [
  { key: 'sunday', label: 'Domingo' },
  { key: 'monday', label: 'Segunda-feira' },
  { key: 'tuesday', label: 'Terça-feira' },
  { key: 'wednesday', label: 'Quarta-feira' },
  { key: 'thursday', label: 'Quinta-feira' },
  { key: 'friday', label: 'Sexta-feira' },
  { key: 'saturday', label: 'Sábado' },
] as const

export type TWeekDay = (typeof WEEK_DAYS)[number]['key']

// Weekly Schedule Types
export interface IWeeklyScheduleItem {
  isSelected: boolean
}

export interface IWeeklySchedule {
  [key: string]: IWeeklyScheduleItem
}

// Organization Type
export const ORGANIZATION_TYPES = [
  {
    value: 'cycle',
    label: 'Ciclo de estudos',
    description: 'Disciplinas em ordem rotativa, sem dias fixos',
  },
  {
    value: 'schedule',
    label: 'Cronograma semanal',
    description: 'Organização fixa de estudos para cada dia da semana',
  },
] as const

export type TOrganizationType = (typeof ORGANIZATION_TYPES)[number]['value']
